import { FC, FormEventHandler, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { AsyncSearch, Loader } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import { useCustomerAPI } from 'hooks/use-customer-api';
import { useOrderApi } from 'hooks/use-order-api';
import { Customer, UnitOrder } from 'types';

const NewOrder: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { searchCustomers } = useCustomerAPI();

  const { postOrders } = useOrderApi();

  const unitNumberInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [orders, setOrders] = useState<Map<number, Partial<UnitOrder>>>(
    new Map().set(new Date().valueOf(), { allowed: searchParams.get('type') === 'allow' })
  );

  const handleAdd = useCallback(
    (allowed?: boolean) => {
      const id = new Date().valueOf();
      setOrders(previous => {
        const ordersCopy = new Map(previous);
        return ordersCopy.set(id, { allowed });
      });
      setTimeout(() => document.getElementById(`${id}`)?.scrollIntoView());
    },
    [setOrders]
  );

  const handleChange = useCallback(
    (id: number, order: Partial<UnitOrder>) => {
      setOrders(previous => {
        const ordersCopy = new Map(previous);
        return ordersCopy.set(id, { ...ordersCopy.get(id), ...order });
      });
    },
    [setOrders]
  );

  const handleDelete = useCallback(
    (id: number) => {
      setOrders(previous => {
        const ordersCopy = new Map(previous);
        ordersCopy.delete(id);
        return ordersCopy;
      });
    },
    [setOrders]
  );

  const handleReset: FormEventHandler = useCallback(
    e => {
      e.preventDefault();
      setOrders(new Map());
      navigate('/orders');
    },
    [navigate, setOrders]
  );

  const handleSubmit: FormEventHandler = useCallback(
    async e => {
      e.preventDefault();
      setLoading(true);
      const response = await postOrders([...orders.values()] as UnitOrder[]);
      setLoading(false);
      if (response) {
        handleReset(e);
      }
    },
    [orders, handleReset, postOrders, setLoading]
  );

  return (
    <Loader loading={loading}>
      <form id="new-order-form" className="page" onReset={handleReset} onSubmit={handleSubmit}>
        <div className="page-header">
          <h1>STOP / ALLOW ORDERS</h1>
          <div className="grid-2">
            <button className="btn-outline" type="reset">
              CANCEL
            </button>
            <button className="btn-primary" type="submit">
              SUBMIT
            </button>
          </div>
        </div>
        <div className="page-content d-flex flex-column g-5 p-5">
          {[...orders.entries()].map(([id, { allowed, customer, unitNumber, notes }], index) => (
            <div className={`card-${!allowed ? 'danger' : 'primary'}`} id={`${id}`} key={id}>
              <div className="card-header d-flex align-items-center">
                {allowed ? (
                  <h4 className="flex-grow-1">{index + 1}. ALLOW ORDER</h4>
                ) : (
                  <h4 className="flex-grow-1">{index + 1}. STOP ORDER</h4>
                )}
                <button
                  className="btn-close"
                  type="button"
                  disabled={orders.size <= 1}
                  onClick={() => handleDelete(id)}
                  data-cy={`order-${index}-delete-button`}
                >
                  <span className="material-icons icon-xlarge">delete</span>
                </button>
              </div>
              <div className="card-body grid-3 auto-rows p-3">
                <div>
                  <h6 className="text-info-dark">ACCOUNT *</h6>
                  <div className="mt-2 w-100">
                    <AsyncSearch<Customer>
                      value={customer}
                      formatValue={c => c?.name ?? ''}
                      formatOption={({ name }) => name}
                      handleSelect={customer => handleChange(id, { customer })}
                      search={searchCustomers}
                      required
                      data-cy={`order-${index}-account-search`}
                    />
                  </div>
                </div>
                <div className="col-span-2 row-span-2 h-100 d-flex flex-column">
                  <h6 className="text-info-dark">NOTES</h6>
                  <textarea
                    className="mt-2 w-100 flex-fill"
                    value={notes ?? ''}
                    onChange={e => handleChange(id, { notes: e.target.value.toUpperCase() })}
                    data-cy={`order-${index}-notes-input`}
                  />
                </div>
                <div>
                  <h6 className="text-info-dark">UNIT NUMBER *</h6>
                  <input
                    className="mt-2 w-100"
                    type="text"
                    ref={unitNumberInputRef}
                    value={unitNumber ?? ''}
                    onChange={e => handleChange(id, { unitNumber: e.target.value.toUpperCase() })}
                    required
                    data-cy={`order-${index}-unit-number-input`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="page-content d-flex flex column g-5 p-5 ml-3">
          <button
            className="btn-outline"
            type="button"
            onClick={() => handleAdd(true)}
            data-cy="add-allow-order-button"
          >
            ADD ALLOW ORDER
          </button>
          <button
            className="btn-outline text-danger"
            type="button"
            onClick={() => handleAdd(false)}
            data-cy="add-stop-order-button"
          >
            ADD STOP ORDER
          </button>
        </div>
      </form>
      <ErrorDialog />
    </Loader>
  );
};

export default NewOrder;
